<template>
  <div
    v-if="!disabled || value"
    class="location"
    :class="{ compact, displayCompletions }"
  >
    <div class="field" :class="{ disabled, hasValue }">
      <div v-if="disabled && hasValue" class="readonly" @click="onClick">
        <div class="icon-container">
          <LocationIcon class="icon" @click="openLocation" />
        </div>
        <div class="value">
          <span>{{ inputValue }}</span>
        </div>
      </div>
      <BaseInput
        v-else-if="!disabled"
        ref="inputEl"
        v-model:value="inputValue"
        class="input"
        :placeholder="placeholder"
        :is-disabled="disabled"
        :is-edited="edited"
        :prevent-default-on-keys="['up', 'down']"
        has-styles
        @focus="onFocus"
        @blur="onBlur"
        @click="onClick"
        @up="selectPreviousLocation"
        @down="selectNextLocation"
        @enter="validateCompletion"
        @meta-enter="validateCompletion"
      >
        <template #left>
          <div class="icon-container">
            <LocationIcon class="icon" @click="openLocation" />
          </div>
        </template>
      </BaseInput>
    </div>
    <ul
      v-if="displayCompletions && locationCompletions.completions.length > 0"
      class="completions"
      @mousedown.stop.prevent
    >
      <li
        v-for="(completion, i) in locationCompletions.completions"
        :key="`completion-${completion.place_id}`"
        class="place-completion"
        :class="{
          selected: i === selected,
        }"
        @click="validateCompletion(completion)"
        @mouseover="mouseOver(i)"
      >
        <span class="text">{{ completion.description }}</span>
      </li>
    </ul>
    <div ref="mapEl" class="hidden" />
  </div>
</template>
<script lang="ts">
import { isEmpty } from "lodash-es";
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import BaseInput from "@/components/base/BaseInput.vue";
import LocationIcon from "@/components/icons/LocationIcon.vue";
import * as inject from "@/components/inject";
import { openLocation as openEventLocation } from "@/event";
import { pause } from "@/utils/pause";
import { arrayProperty, property } from "@/utils/unknown";

// The Google Places Autocomplete library is loaded at runtime via a <script>
// tag, so we model only the small slice of its shape that we consume here
// instead of relying on the ambient `google.maps` types in every build.
type AutocompletePrediction = {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
};

type LocationCompletions = {
  completions: AutocompletePrediction[];
  requestTs: Date;
};

type AutocompleteRequestLite = {
  input: string;
  types?: string[];
};

type FetchAutocompleteSuggestions = (
  request: AutocompleteRequestLite
) => unknown;

const autocompleteSuggestionFetcher = (
  value: unknown
): FetchAutocompleteSuggestions | null => {
  if (typeof value !== "function") return null;
  const fetch = Reflect.get(value, "fetchAutocompleteSuggestions");
  if (typeof fetch !== "function") return null;
  return (request) => Reflect.apply(fetch, value, [request]);
};

const predictionText = (prediction: unknown): string => {
  const text = property(prediction, "text");
  const toString = property(text, "toString");
  if (typeof toString !== "function") return "";
  const result: unknown = Reflect.apply(toString, text, []);
  return typeof result === "string" ? result : "";
};

const autocompletePredictions = (response: unknown): AutocompletePrediction[] =>
  (arrayProperty(response, "suggestions") ?? []).flatMap((suggestion) => {
    const prediction = property(suggestion, "placePrediction");
    if (prediction === undefined) return [];
    const description = predictionText(prediction);
    const placeId = property(prediction, "placeId");
    const types = arrayProperty(prediction, "types")?.filter(
      (type): type is string => typeof type === "string"
    );
    return [
      {
        description,
        place_id: typeof placeId === "string" ? placeId : "",
        structured_formatting: {
          main_text: description,
          secondary_text: "",
        },
        types: types ?? [],
      },
    ];
  });

export default defineComponent({
  components: {
    BaseInput,
    LocationIcon,
  },
  props: {
    clickable: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    edited: { type: Boolean, default: false },
    placeholder: { type: String, default: "Location…" },
    types: {
      // Types allowed for autocomplete
      // see https://developers.google.com/maps/documentation/places/web-service/supported_types#table3.
      type: Array as PropType<string[]>,
      default: () => [],
    },
    value: { type: String, required: true },
  },
  emits: {
    "update:value": (_: string) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const { clickable, value, disabled } = toRefs(props);
    const inputEl = ref<InstanceType<typeof BaseInput>>();
    const blurInput = () => pause(() => inputEl.value?.blur());
    const isInputFocused = computed<boolean>(() => !!inputEl.value?.isFocused);
    const inputValue = computed({
      get: () => value.value,
      set(v) {
        emit("update:value", v);
        if (isInputFocused.value) updatePredictions(v);
      },
    });
    const locationCompletions = ref<LocationCompletions>({
      completions: [],
      requestTs: new Date(),
    });
    const selected = ref<number>(0);
    const displayCompletions = ref(false);
    const mouseOver = (index: number) => (selected.value = index);
    const onFocus = () => (displayCompletions.value = true);
    const onBlur = () => pause(resetCompletion);
    const emptyCompletions = () => {
      locationCompletions.value.completions = [];
      selected.value = 0;
    };
    const validateCompletion = (
      place: AutocompletePrediction = locationCompletions.value.completions[
        selected.value
      ]
    ) => {
      if (place) inputValue.value = place.description;
      blurInput();
    };
    const resetCompletion = () => {
      displayCompletions.value = false;
      emptyCompletions();
    };
    const hasValue = computed(() => !isEmpty(value.value));
    const selectPreviousLocation = () => {
      selected.value = Math.max(selected.value - 1, 0);
    };
    const selectNextLocation = () => {
      selected.value = Math.min(
        selected.value + 1,
        (locationCompletions.value.completions.length || 0) - 1
      );
    };
    const openLocation = () => {
      if (clickable.value && hasValue.value) {
        openEventLocation(value.value, controller);
      }
    };
    // Google
    const requestBackoffMs = 210;
    const mapEl = ref<HTMLElement>();
    const autocompletePendingRequestTimeout = ref<ReturnType<
      typeof setTimeout
    > | null>(null);
    const fetchAutocompleteSuggestions =
      ref<FetchAutocompleteSuggestions | null>(null);
    const onLibLoaded = async () => {
      if (mapEl.value) {
        const candidate = window.google?.maps?.places?.AutocompleteSuggestion;
        fetchAutocompleteSuggestions.value =
          autocompleteSuggestionFetcher(candidate);
        return;
      }
    };
    const loadLib = (callback: () => void) => {
      if ("google" in window && "maps" in window.google) {
        callback();
        return;
      }
      if (!("google" in window)) {
        Object.assign(window, { google: {} });
        const script = document.createElement("script");
        document.body.appendChild(script);
        // @ts-ignore
        window.ggmapscb = callback;
        script.src =
          "https://maps.googleapis.com/maps/api/js?libraries=places&key=AIzaSyD1OHtfVQJ4cjyITP8Bjdih42tPg-m6d8I&callback=ggmapscb&loading=async";
        script.async = true;
      }
    };
    onMounted(() => {
      if (!disabled.value) {
        loadLib(onLibLoaded);
      }
    });
    const updatePredictions = (text: string) => {
      const fetch = fetchAutocompleteSuggestions.value;
      if (!fetch) return;

      if (text === "") {
        emptyCompletions();
      } else {
        if (!displayCompletions.value) {
          displayCompletions.value = true;
        }
        // Evaluation of how good the current completions still are:
        // The types returned by Google are super useful, they tell how good the match is,
        // we could recompute these metrics and only call when match is not so good anymore.
        // For sake of going fast, we are going to use a time hysteresis instead, where we only keep
        // the last input change made within the last Xms.
        if (autocompletePendingRequestTimeout.value) {
          clearTimeout(autocompletePendingRequestTimeout.value);
        }
        const call = () => {
          const req: AutocompleteRequestLite = {
            input: text,
          };
          if (props.types && props.types.length > 0) {
            req.types = props.types;
          }
          Promise.resolve(fetch(req))
            .then((response) => {
              const preds = autocompletePredictions(response);

              selected.value = 0;
              locationCompletions.value = {
                completions: preds,
                requestTs: new Date(),
              };
            })
            .catch(() => {
              // Silently handle errors
            });
        };
        const timeSinceLastRequest =
          Date.now() - locationCompletions.value.requestTs.getTime();
        if (timeSinceLastRequest > requestBackoffMs) {
          call();
        } else {
          autocompletePendingRequestTimeout.value = setTimeout(
            call,
            requestBackoffMs
          );
        }
      }
    };
    const isCmdPressed = (event: MouseEvent): boolean =>
      event.metaKey || event.ctrlKey;
    const onClick = (event: MouseEvent) => {
      if (
        hasValue.value &&
        clickable.value &&
        (disabled.value || isCmdPressed(event))
      )
        return openLocation();
    };
    return {
      displayCompletions,
      hasValue,
      inputEl,
      inputValue,
      locationCompletions,
      mapEl,
      mouseOver,
      onBlur,
      onClick,
      onFocus,
      openLocation,
      selected,
      selectNextLocation,
      selectPreviousLocation,
      validateCompletion,
    };
  },
});
</script>

<style lang="scss" scoped>
.location {
  display: flex;
  flex-direction: column;
  position: relative;

  .hidden {
    display: none;
  }
  &.compact {
    .field {
      column-gap: 4px;
      flex-direction: row-reverse;
    }
    .icon-container {
      margin-inline-start: 2px;
    }
  }
  .field {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 16px;

    .icon-container {
      border-radius: 100%;
      padding: 2px;
      margin: 0 1px;
      display: flex;
      align-items: center;
      justify-items: center;
      background-color: transparent;
      @include transition(background-color 200ms ease-out);

      .icon {
        width: 15px;
        height: 15px;
        :deep(path.fill) {
          @include themed() {
            fill: t($grey-50);
          }
        }
      }
    }

    .readonly {
      display: flex;
      flex: 1;
      flex-direction: row;
      column-gap: 2px;
      padding: 5px 0 4px;
      padding-inline: 4px 8px;
      align-items: center;
      overflow: hidden;
      height: 19px;
      margin-bottom: 2px;
      @include transition(color 200ms ease-out);
      @include themed() {
        color: t($grey-70);
      }
      &:hover {
        cursor: pointer;
        @include themed() {
          color: t($tertiary-50);
        }
        .icon-container {
          @include themed() {
            background-color: t($tertiary-10);
          }
          .icon {
            :deep(path.fill) {
              @include themed() {
                fill: t($tertiary-50);
              }
            }
          }
        }

        .value span {
          @include themed() {
            border-color: t($tertiary-50);
          }
        }
      }
      .value {
        display: flex;
        align-items: center;
        height: 18px;
        overflow: hidden;
        span {
          width: auto;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          border-bottom: 0.5px solid transparent;
        }
      }
    }

    &.hasValue {
      .icon-container {
        cursor: pointer;
        &:hover {
          @include themed() {
            background-color: t($tertiary-10);
          }
          .icon {
            :deep(path.fill) {
              @include themed() {
                fill: t($tertiary-50);
              }
            }
          }
        }
      }
    }

    .input {
      :deep(.slotLeft) {
        padding-block: 0;
        padding-inline-end: 4px;
        padding-inline-start: 2px;
      }
      &.isEdited {
        @include themed() {
          color: t($tertiary-50);
        }
      }
      :deep(input) {
        @include transition(color 200ms ease-out);
      }
    }
  }
  .completions {
    position: absolute;
    top: calc(100% + 4px);
    inset-inline-start: 0;
    width: calc(100% - 17px);
    margin: 0;
    padding: 4px;
    border-radius: $border-radius-l;
    box-shadow: $tooltip;
    z-index: $z-index-content;
    @include themed() {
      background: t($bg);
      border: 1px solid t($grey-10);
    }
    .place-completion {
      padding: 6px 8px;
      cursor: default;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      border-radius: $border-radius-m;
      @include themed() {
        color: t($grey-70);
      }
      &.selected {
        @include themed() {
          color: t($primary);
          background: t($grey-10);
        }
      }

      .text {
        @extend %font-caption-regular;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  [contenteditable][placeholder]:empty:before {
    content: attr(placeholder);
    position: absolute;
    background-color: transparent;

    @include themed() {
      color: t($grey-50);
    }
  }
}
</style>
